const makeUrl = (pathname: string): string => {
  if (import.meta.env.REACT_APP_BACKEND_URL === "/") {
    // we're in a CORS-less mode, paths to the frontend will work
    return fullyQualifiedUrl(pathname);
  }
  if (import.meta.env.REACT_APP_BACKEND_URL) {
    const url = new URL(import.meta.env.REACT_APP_BACKEND_URL);
    url.pathname = pathname;
    return url.href;
  }
  throw new Error("please define REACT_APP_BACKEND_URL");
};

export type ImageResourceType = "Profile" | "room" | "event" | "organization" | "Bubbles";
export const makeImageResourceAvatarUrl = (resourceType: ImageResourceType, resourceId: string): string =>
  makeUrl(`/api/v1/${resourceType}/${resourceId}/picture`);
export const makeProfileAvatarUrl = (userId: string): string => makeImageResourceAvatarUrl("Profile", userId);
export const makeRoomAvatarUrl = (roomId: string): string => makeImageResourceAvatarUrl("room", roomId);
export const makeEventAvatarUrl = (eventId: string): string => makeImageResourceAvatarUrl("event", eventId);
export const makeOrganizationAvatarUrl = (organizationId: string): string =>
  makeUrl(`/api/v1/Organization/${organizationId}/picture`);
export const makeBubbleAvatarUrl = (bubbleId: string): string => makeImageResourceAvatarUrl("Bubbles", bubbleId);

const BUBBLE_AVATAR_CACHE_BUSTER_PREFIX = "crowdcoursing.bubbleAvatarCacheBuster.";

export function getBubblePictureIdFromAvatarUri(avatarUri: string): string | undefined {
  const match = /\/Bubbles\/([^/?#]+)\/picture/i.exec(avatarUri);
  return match?.[1];
}

export function readBubbleAvatarCacheBuster(bubbleId: string): string | undefined {
  return localStorage.getItem(`${BUBBLE_AVATAR_CACHE_BUSTER_PREFIX}${bubbleId}`) ?? undefined;
}

export function writeBubbleAvatarCacheBuster(bubbleId: string, timestamp: number | string) {
  localStorage.setItem(`${BUBBLE_AVATAR_CACHE_BUSTER_PREFIX}${bubbleId}`, String(timestamp));
}

const ORGANIZATION_AVATAR_CACHE_BUSTER_PREFIX = "crowdcoursing.organizationAvatarCacheBuster.";

export function readOrganizationAvatarCacheBuster(organizationId: string): string | undefined {
  return localStorage.getItem(`${ORGANIZATION_AVATAR_CACHE_BUSTER_PREFIX}${organizationId}`) ?? undefined;
}

export function writeOrganizationAvatarCacheBuster(organizationId: string, timestamp: number | string) {
  localStorage.setItem(`${ORGANIZATION_AVATAR_CACHE_BUSTER_PREFIX}${organizationId}`, String(timestamp));
}

export function withBubbleAvatarCacheBust(avatarUri: string, bubbleId: string, timestamp?: number | string): string {
  const cacheBuster = timestamp ?? readBubbleAvatarCacheBuster(bubbleId);
  if (!cacheBuster) {
    return avatarUri;
  }

  const url = new URL(avatarUri);
  url.searchParams.set("t", String(cacheBuster));
  return url.toString();
}

export function withOrganizationAvatarCacheBust(
  avatarUri: string,
  organizationId: string,
  timestamp?: number | string,
): string {
  const cacheBuster = timestamp ?? readOrganizationAvatarCacheBuster(organizationId);
  if (!cacheBuster) {
    return avatarUri;
  }

  const url = new URL(avatarUri);
  url.searchParams.set("t", String(cacheBuster));
  return url.toString();
}

const PROFILE_AVATAR_CACHE_BUSTER_PREFIX = "crowdcoursing.profileAvatarCacheBuster.";

export function readProfileAvatarCacheBuster(userId: string): string | undefined {
  return localStorage.getItem(`${PROFILE_AVATAR_CACHE_BUSTER_PREFIX}${userId}`) ?? undefined;
}

export function writeProfileAvatarCacheBuster(userId: string, timestamp: number | string) {
  localStorage.setItem(`${PROFILE_AVATAR_CACHE_BUSTER_PREFIX}${userId}`, String(timestamp));
}

export function withProfileAvatarCacheBust(avatarUri: string, userId: string, timestamp?: number | string): string {
  const cacheBuster = timestamp ?? readProfileAvatarCacheBuster(userId);
  if (!cacheBuster) {
    return avatarUri;
  }

  const url = new URL(avatarUri);
  url.searchParams.set("t", String(cacheBuster));
  return url.toString();
}

/** @deprecated use fullyQualifiedUrl and `services/router/Route` */
export const makeRoomUrl = (roomId: string): string => {
  if (window.location.href) {
    const url = new URL(window.location.href);
    url.pathname = `/rooms/${roomId}`;
    return url.href;
  }
  throw new Error("please define REACT_APP_BACKEND_URL");
};

/** builds a full url to the given path, intended to be used with
 * `services/router/Route` */
export const fullyQualifiedUrl = (pathname: string): string => {
  const url = new URL(window.location.href);
  url.pathname = pathname;
  return url.href;
};

/** sleeps for the given amount of time */
export const sleep = (delayMillis: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, delayMillis);
  });

/** retry an async operation with delays */
export const withRetries = async (callback: () => Promise<void>, retries = 5, delayMillis = 1000): Promise<boolean> => {
  for (let i = 0; i < retries; i++) {
    try {
      await callback();
      return true;
    } catch (err) {
      console.warn("unexpected error", err);
      await sleep(delayMillis); // wait a little before trying again
    }
  }
  return false;
};

/** is the given token expired? */
export const isTokenExpired = (token: string): boolean => {
  try {
    // decode JWT, find the expiration date
    const exp = new Date(JSON.parse(atob(token.split(".")[1])).exp * 1000);
    return new Date() > exp;
  } catch {
    return true;
  }
};

export const isValidUrl = (url: string): boolean => {
  try {
    new URL(url);
  } catch {
    return false;
  }

  return true;
};
